import { Form } from "antd";
import type { FormInstance } from "antd/es/form/Form";
import { blankToUndefined, optionalNonNegativeValidator } from "components/Form/components/Validations/validationRules/NonNegative";
import i18n from "i18next";
import { type FC, useMemo } from "react";

import type { TerminalBatchAddEntity } from "src/app/features/Terminal/types/TerminalBatchAddEntity";
import { TerminalType } from "src/app/features/TerminalProfile/types/TerminalProfileType";
import { useLoadDatasourceConfigurationsQuery } from "../../../../../components";
import { DrawerForm } from "../../../../../components/DrawerForm/DrawerForm";
import { SelectDynamic } from "../../../../../components/Form/formComponents";
import { NumberInput } from "../../../../../components/Form/formComponents/Number/NumberInput";
import type { SearchFormProps } from "../../../../../components/Form/types/types";
import { siteSelectConfig, terminalProfileIPTSelectConfig } from "../../../../../components/SearchForm/FormSearchUtils";

type AvailableTypesResponse = {
    items: { key: string; value: string }[];
};

const hasType = (items: AvailableTypesResponse["items"], type: TerminalType) => items.some((it) => it.value === type);

type TerminalBatchAddFormProps = SearchFormProps<TerminalBatchAddEntity> & { form?: FormInstance };

export const TerminalBatchAddForm: FC<TerminalBatchAddFormProps> = ({ searchCallback, form }) => {
    // get terminal types available to this DG in order to render only relevant fields
    const { data } = useLoadDatasourceConfigurationsQuery("terminal-type");
    const availableTypes = useMemo(() => (data as AvailableTypesResponse | undefined)?.items ?? [], [data]);

    const showIPT = hasType(availableTypes, TerminalType.ISIPT);
    const showAndroid = hasType(availableTypes, TerminalType.ISANDROID);
    const showOPT = hasType(availableTypes, TerminalType.ISOPT);
    const showUnattended = hasType(availableTypes, TerminalType.ISUNATTENDED);
    const showEcommerce = hasType(availableTypes, TerminalType.ISECOMMERCE);

    return (
        <DrawerForm onSubmit={searchCallback} form={form} useSubmitButton={true}>
            <Form.Item
                name="siteId"
                label={i18n.t("terminalSearchForm.criteriaBadges.siteId")}
                rules={[{ required: true, message: i18n.t("common.input.required.error") }]}
            >
                <SelectDynamic name="siteId" label={i18n.t("terminalSearchForm.criteriaBadges.siteId")} fieldConfig={siteSelectConfig} />
            </Form.Item>

            {showIPT && (
                <>
                    <Form.Item
                        label={i18n.t("terminal.batchAdd.numberOfIpt")}
                        name="numberOfIpt"
                        rules={[{ validator: optionalNonNegativeValidator }]}
                        getValueFromEvent={blankToUndefined}
                    >
                        <NumberInput name="numberOfIpt" readOnly={false} />
                    </Form.Item>
                    <Form.Item name="profileIpt" label={i18n.t("terminal.batchAdd.profileIpt")}>
                        <SelectDynamic
                            name="profileIpt"
                            label={i18n.t("terminal.batchAdd.profileIpt")}
                            fieldConfig={terminalProfileIPTSelectConfig(TerminalType.ISIPT)}
                        />
                    </Form.Item>
                </>
            )}

            {showAndroid && (
                <>
                    <Form.Item
                        label={i18n.t("terminal.batchAdd.numberOfAndroid")}
                        name="numberOfAndroid"
                        rules={[{ validator: optionalNonNegativeValidator }]}
                        getValueFromEvent={blankToUndefined}
                    >
                        <NumberInput name="numberOfAndroid" readOnly={false} />
                    </Form.Item>
                    <Form.Item name="profileAndroid" label={i18n.t("terminal.batchAdd.profileAndroid")}>
                        <SelectDynamic
                            name="profileAndroid"
                            label={i18n.t("terminal.batchAdd.profileAndroid")}
                            fieldConfig={terminalProfileIPTSelectConfig(TerminalType.ISANDROID)}
                        />
                    </Form.Item>
                </>
            )}

            {showOPT && (
                <>
                    <Form.Item
                        label={i18n.t("terminal.batchAdd.numberOfOpt")}
                        name="numberOfOpt"
                        rules={[{ validator: optionalNonNegativeValidator }]}
                        getValueFromEvent={blankToUndefined}
                    >
                        <NumberInput name="numberOfOpt" readOnly={false} />
                    </Form.Item>
                    <Form.Item name="profileOpt" label={i18n.t("terminal.batchAdd.profileOpt")}>
                        <SelectDynamic
                            name="profileOpt"
                            label={i18n.t("terminal.batchAdd.profileOpt")}
                            fieldConfig={terminalProfileIPTSelectConfig(TerminalType.ISOPT)}
                        />
                    </Form.Item>
                </>
            )}

            {showUnattended && (
                <>
                    <Form.Item
                        label={i18n.t("terminal.batchAdd.numberOfUnattended")}
                        name="numberOfUnattended"
                        rules={[{ validator: optionalNonNegativeValidator }]}
                        getValueFromEvent={blankToUndefined}
                    >
                        <NumberInput name="numberOfUnattended" readOnly={false} />
                    </Form.Item>
                    <Form.Item name="profileUnattended" label={i18n.t("terminal.batchAdd.profileUnattended")}>
                        <SelectDynamic
                            name="profileUnattended"
                            label={i18n.t("terminal.batchAdd.profileUnattended")}
                            fieldConfig={terminalProfileIPTSelectConfig(TerminalType.ISUNATTENDED)}
                        />
                    </Form.Item>
                </>
            )}

            {showEcommerce && (
                <>
                    <Form.Item
                        label={i18n.t("terminal.batchAdd.numberOfEcommerce")}
                        name="numberOfEcommerce"
                        rules={[{ validator: optionalNonNegativeValidator }]}
                        getValueFromEvent={blankToUndefined}
                    >
                        <NumberInput name="numberOfEcommerce" readOnly={false} />
                    </Form.Item>
                    <Form.Item name="profileEcommerce" label={i18n.t("terminal.batchAdd.profileEcommerce")}>
                        <SelectDynamic
                            name="profileEcommerce"
                            label={i18n.t("terminal.batchAdd.profileEcommerce")}
                            fieldConfig={terminalProfileIPTSelectConfig(TerminalType.ISECOMMERCE)}
                        />
                    </Form.Item>
                </>
            )}
        </DrawerForm>
    );
};
